import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  PaperList,
  PaperListRow,
  PaperUse,
  Supplier,
  WaitingForFacturaList,
} from "../api/types";
import { ReasonModal } from "../components/ui/ReasonModal";
import { calendarDayText, dateText, dateTimeText, money } from "../i18n/intl";
import { useAuth } from "../state/AuthContext";
import { businessToday } from "../utils/businessDay";
import { paperHeadline, paperPills, recentMonths } from "../utils/paperList";
import { PAPER_USES, paperUseLabel } from "../utils/paperUse";
import type { DashboardOutletContext } from "./dashboardOutletContext";

type View = "all" | "waiting";
type WaitingRow = WaitingForFacturaList["rows"][number];

const MONTHS_OFFERED = 24;

function monthText(ym: string): string {
  const [year, month] = ym.split("-").map(Number);
  return dateText(new Date(Date.UTC(year, month - 1, 1)), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function isPaperUse(value: string | null): value is PaperUse {
  return value !== null && (PAPER_USES as readonly string[]).includes(value);
}

/**
 * G-131 — every uploaded supplier paper in one place (owner ruling
 * 2026-10-09). Before it, a paper was reachable only from its delivery,
 * right after upload, or from the owner's Finances card — a manager who left
 * a factura's page could not get back to it, and the factura's page is where
 * it is linked to its albaranes.
 *
 * Two views: every paper (filter by type, shop, supplier, month, number), and
 * what is still waiting for a factura — albaranes and deliveries typed by
 * hand — which the home page's Needs attention row opens. Filters live in
 * the address, so Back from a paper returns to the same list.
 */
export function SupplierPapersPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const { locations } = useOutletContext<DashboardOutletContext>();
  const [params, setParams] = useSearchParams();

  const view: View = params.get("view") === "waiting" ? "waiting" : "all";
  const typeParam = params.get("type");
  const paperUse = isPaperUse(typeParam) ? typeParam : null;
  const supplierId = params.get("supplier") ?? "";
  const locationId = isOwner ? (params.get("shop") ?? "") : "";
  const month = params.get("month") ?? "";
  const search = params.get("q") ?? "";

  const [searchDraft, setSearchDraft] = useState(search);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [list, setList] = useState<PaperList | null>(null);
  const [waiting, setWaiting] = useState<WaitingForFacturaList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [dismissing, setDismissing] = useState<WaitingRow | null>(null);
  const [dismissBusy, setDismissBusy] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);

  const months = useMemo(() => recentMonths(businessToday(), MONTHS_OFFERED), []);

  const setParam = (key: string, value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    void api
      .listSuppliers()
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  // The search box asks the server a moment after typing stops …
  useEffect(() => {
    const next = searchDraft.trim();
    if (next === search) return;
    const timer = window.setTimeout(() => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next) updated.set("q", next);
          else updated.delete("q");
          return updated;
        },
        { replace: true },
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchDraft, search, setParams]);

  // … and follows the address when something else changes it (Back, a supplier link).
  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  // The papers list follows the filters …
  useEffect(() => {
    if (view !== "all") return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listPapers({
        paperUse: paperUse ?? undefined,
        supplierId: supplierId || undefined,
        locationId: locationId || undefined,
        month: month || undefined,
        search: search || undefined,
      })
      .then((papers) => {
        if (!cancelled) setList(papers);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the papers");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, paperUse, supplierId, locationId, month, search]);

  // … the waiting list does not: it loads once, and again after a dismissal.
  useEffect(() => {
    let cancelled = false;
    api
      .listWaitingForFactura()
      .then((waitingList) => {
        if (!cancelled) setWaiting(waitingList);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load what waits for a factura");
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const dismiss = async (reason: string | undefined) => {
    if (!dismissing || !reason) return;
    setDismissBusy(true);
    setDismissError(null);
    try {
      await api.dismissDeliveryFacturaWait(dismissing.id, reason);
      setDismissing(null);
      setReloadTick((t) => t + 1);
    } catch (err: unknown) {
      setDismissError(err instanceof Error ? err.message : "Could not close the wait");
    } finally {
      setDismissBusy(false);
    }
  };

  const waitingCount = waiting?.count ?? null;

  return (
    <section className="papers-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Supplier papers</h2>
          <p className="page-subtitle">
            Every albarán and factura uploaded. Open one to schedule its delivery, link a factura to its
            albaranes, or see what it counted.
          </p>
        </div>
        <Link to="/app/invoices/upload" className="button">
          Upload a paper
        </Link>
      </div>

      <div className="dlv-chips" role="group" aria-label="View">
        <button
          type="button"
          className="dlv-chip"
          aria-pressed={view === "all"}
          onClick={() => setParam("view", null)}
        >
          All papers
        </button>
        <button
          type="button"
          className="dlv-chip"
          aria-pressed={view === "waiting"}
          onClick={() => setParam("view", "waiting")}
        >
          Waiting for a factura{waitingCount !== null ? ` (${waitingCount})` : ""}
        </button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {view === "all" ? (
        <>
          <div className="card papers-filters">
            <div className="dlv-chips" role="group" aria-label="Type">
              <button
                type="button"
                className="dlv-chip"
                aria-pressed={paperUse === null}
                onClick={() => setParam("type", null)}
              >
                All types
              </button>
              {PAPER_USES.map((use) => (
                <button
                  key={use}
                  type="button"
                  className="dlv-chip"
                  aria-pressed={paperUse === use}
                  onClick={() => setParam("type", use)}
                >
                  {paperUseLabel(use)}
                </button>
              ))}
            </div>
            <div className="papers-filter-fields">
              {isOwner ? (
                <div className="field">
                  <label htmlFor="papers-shop">Shop</label>
                  <select id="papers-shop" value={locationId} onChange={(e) => setParam("shop", e.target.value || null)}>
                    <option value="">All shops</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="field">
                <label htmlFor="papers-supplier">Supplier</label>
                <select
                  id="papers-supplier"
                  value={supplierId}
                  onChange={(e) => setParam("supplier", e.target.value || null)}
                >
                  <option value="">All suppliers</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="papers-month">Month</label>
                <select id="papers-month" value={month} onChange={(e) => setParam("month", e.target.value || null)}>
                  <option value="">All months</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {monthText(m)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="papers-search">Number or file name</label>
                <input
                  id="papers-search"
                  type="search"
                  value={searchDraft}
                  maxLength={80}
                  placeholder="e.g. A261572"
                  onChange={(e) => setSearchDraft(e.target.value)}
                />
              </div>
            </div>
          </div>

          {loading && !list ? (
            <p className="muted dlv-empty">Loading…</p>
          ) : list && list.rows.length === 0 ? (
            <p className="muted dlv-empty">No papers match.</p>
          ) : list ? (
            <>
              <p className="muted papers-count">
                {list.total > list.rows.length
                  ? `Showing the newest ${list.rows.length} of ${list.total} papers. Pick a month or a supplier to see the rest.`
                  : list.total === 1
                    ? "1 paper"
                    : `${list.total} papers`}
              </p>
              <div className="dlv-card-list">
                {list.rows.map((row) => (
                  <PaperCard key={row.id} row={row} />
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : (
        <>
          <p className="muted papers-note">
            An albarán counts no money until its factura is linked: open the factura and link it there. A
            delivery typed by hand waits too, once something of it has arrived. When no factura will ever come
            for it, the owner can close the wait; its IVA then counts as not deductible.
          </p>
          {!waiting ? (
            <p className="muted dlv-empty">Loading…</p>
          ) : waiting && waiting.rows.length === 0 ? (
            <p className="muted dlv-empty">Nothing is waiting for a factura.</p>
          ) : waiting ? (
            <div className="dlv-card-list">
              {waiting.rows.map((row) => (
                <WaitingCard
                  key={`${row.kind}-${row.id}`}
                  row={row}
                  canDismiss={isOwner && row.kind === "delivery"}
                  onDismiss={() => {
                    setDismissError(null);
                    setDismissing(row);
                  }}
                  onShowSupplier={() =>
                    setParams(new URLSearchParams({ supplier: row.supplier.id }), { replace: false })
                  }
                />
              ))}
            </div>
          ) : null}
        </>
      )}

      {dismissing ? (
        <ReasonModal
          title="No factura for this delivery?"
          message={`${dismissing.supplier.name}, ${dismissing.label}. It leaves the waiting list. Its typed purchases keep counting as cost, but their IVA is shown as not deductible. To link a factura later, reopen the wait on the delivery first.`}
          reasonMode="required"
          reasonLabel="Why will no factura come?"
          reasonPlaceholder="e.g. cash purchase at the market"
          minReasonLength={3}
          confirmLabel="Close the wait"
          busy={dismissBusy}
          error={dismissError}
          onConfirm={dismiss}
          onClose={() => setDismissing(null)}
        />
      ) : null}
    </section>
  );
}

function PaperCard({ row }: { row: PaperListRow }) {
  const headline = paperHeadline(row);
  const pills = paperPills(row);
  const waits = row.waitingForFactura;
  return (
    <Link
      to={`/app/invoices/${row.id}`}
      className={`paper-card${waits ? " paper-card--waiting" : ""}`}
    >
      <span className="paper-card-title">
        {row.supplier.name} · {row.label}
      </span>
      <span className="paper-card-money">
        {headline ? (
          <>
            <b>{money(headline.amount)}</b>
            <span>{headline.label}</span>
          </>
        ) : (
          <span>no total read</span>
        )}
      </span>
      <span className="paper-card-meta">
        {paperUseLabel(row.paperUse)} · {row.invoiceDate ? calendarDayText(row.invoiceDate) : "no date"} ·{" "}
        {row.location.name}
      </span>
      <span className="paper-card-meta">
        Uploaded {dateTimeText(new Date(row.uploadedAt))} by {row.uploadedBy.fullName}
      </span>
      {pills.length > 0 ? (
        <span className="paper-card-foot">
          {pills.map((p) => (
            <span key={p.key} className={`paper-pill paper-pill--${p.tone}`}>
              {p.text}
            </span>
          ))}
        </span>
      ) : null}
    </Link>
  );
}

function WaitingCard({
  row,
  canDismiss,
  onDismiss,
  onShowSupplier,
}: {
  row: WaitingRow;
  canDismiss: boolean;
  onDismiss: () => void;
  onShowSupplier: () => void;
}) {
  const to = row.kind === "albaran" ? `/app/invoices/${row.id}` : `/app/deliveries/${row.id}`;
  return (
    <div className="paper-card paper-card--waiting">
      <Link to={to} className="paper-card-title">
        {row.supplier.name} · {row.kind === "albaran" ? `Albarán ${row.label}` : `Delivery typed by hand, ${row.label}`}
      </Link>
      <span className="paper-card-money">
        {row.totalEur !== null ? (
          <>
            <b>{money(row.totalEur)}</b>
            <span>before IVA</span>
          </>
        ) : (
          <span>no total read</span>
        )}
      </span>
      <span className="paper-card-meta">
        {row.date ? calendarDayText(row.date) : "no date"} · {row.location.name} ·{" "}
        {row.daysWaiting === 0 ? "since today" : row.daysWaiting === 1 ? "waiting 1 day" : `waiting ${row.daysWaiting} days`}
      </span>
      <span className="paper-card-foot">
        <button type="button" className="button tinted" onClick={onShowSupplier}>
          {row.supplier.name}&apos;s papers
        </button>
        {canDismiss && row.noFacturaBlockedBy ? (
          // G-137 (OD-184): with no factura coming, the delivery's own document is
          // the only paper — the server refuses the close until one is attached.
          <Link to={to} className="button tinted">
            Add a document first
          </Link>
        ) : canDismiss ? (
          <button type="button" className="button tinted" onClick={onDismiss}>
            No factura will come
          </button>
        ) : null}
      </span>
    </div>
  );
}
